/**
 * Types for AI battle analysis and move quality classification
 */

import { Position, IMove, GameState } from "../../types";
import { AIPlayerType } from "../Core/IAIPlayer";

/**
 * Move quality classifications
 */
export enum MoveQuality {
  BRILLIANT = "Brilliant", // Exceptional move, best in position
  GOOD = "Good", // Strong move, slightly better than alternatives
  REGULAR = "Regular", // Normal move, reasonable choice
  BAD = "Bad", // Suboptimal move, loses advantage
  BLUNDER = "Blunder", // Critical mistake, loses material or game
}

/**
 * Detailed analysis of a single move
 */
export interface MoveAnalysis {
  fromPosition: Position;
  move: IMove;
  quality: MoveQuality;
  evaluation: number; // Position evaluation after this move
  bestMoveEvaluation: number; // Evaluation of the best move
  evaluationDelta: number; // Difference between this move and best move
  reasoning: string; // Explanation of the quality rating
  alternativeMoves?: Array<{
    fromPosition: Position;
    move: IMove;
    evaluation: number;
  }>;
}

/**
 * Analysis of a complete game
 */
export interface GameAnalysis {
  gameId: string;
  lightPlayer: AIPlayerType;
  darkPlayer: AIPlayerType;
  winner: "light" | "dark" | "draw";
  totalMoves: number;
  lightMoves: MoveAnalysis[];
  darkMoves: MoveAnalysis[];
  summary: GameSummary;
}

/**
 * Summary statistics for a player in a game
 */
export interface PlayerSummary {
  player: AIPlayerType;
  totalMoves: number;
  brilliantMoves: number;
  goodMoves: number;
  regularMoves: number;
  badMoves: number;
  blunders: number;
  averageEvaluation: number;
  averageThinkingTime: number;
  accuracy: number; // Percentage of moves that were Brilliant/Good/Regular
}

/**
 * Overall game summary
 */
export interface GameSummary {
  lightSummary: PlayerSummary;
  darkSummary: PlayerSummary;
  gameLength: number; // Total number of moves
  decisiveMove?: {
    moveNumber: number;
    player: "light" | "dark";
    quality: MoveQuality;
    evaluationSwing: number;
  };
  openingEvaluation: number;
  midgameEvaluation: number;
  endgameEvaluation: number;
}

/**
 * Battle report comparing multiple games between AIs
 */
export interface BattleReport {
  battleId: string;
  timestamp: number;
  lightPlayer: AIPlayerType;
  darkPlayer: AIPlayerType;
  gamesPlayed: number;
  lightWins: number;
  darkWins: number;
  draws: number;
  games: GameAnalysis[];
  overallStats: {
    light: PlayerSummary;
    dark: PlayerSummary;
  };
  headToHeadComparison: {
    winRate: { light: number; dark: number };
    averageGameLength: number;
    averageAccuracy: { light: number; dark: number };
    blunderComparison: { light: number; dark: number };
  };
}

/**
 * Configuration for deep analysis
 */
export interface DeepAnalysisConfig {
  maxDepth: number; // Maximum search depth for evaluation
  maxTime: number; // Maximum time per position in ms
  useGPU: boolean; // Whether to use GPU acceleration
  saveToFile: boolean; // Whether to save analysis to file
  outputPath?: string; // Path to save analysis
}

/**
 * Result of deep position analysis
 */
export interface DeepPositionAnalysis {
  gameState: GameState;
  bestMove: {
    fromPosition: Position;
    move: IMove;
    evaluation: number;
    principalVariation: Array<{
      fromPosition: Position;
      move: IMove;
    }>;
  };
  topMoves: Array<{
    fromPosition: Position;
    move: IMove;
    evaluation: number;
    depth: number;
  }>;
  positionEvaluation: number;
  depth: number;
  nodesSearched: number;
  timeSpent: number;
}